// Jobs View page
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../atoms";
import { jobService } from "../../services/jobService";
import { userService } from "../../services/userService";
import { resolveImageUrl } from "../../utils/imageUrl";
import { AdminLayout, Lightbox } from "../organisms";
import {
  formatDate,
  getStatusColor,
  formatStatus,
} from "../../utils/jobStatus";

export const JobsView = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { images: string[], index: number } | null
  const [tooltip, setTooltip] = useState(null); // { text, x, y } | null
  const [selectedJob, setSelectedJob] = useState(null); // job object shown in modal
  const [completionPending, setCompletionPending] = useState(null); // { jobId } when awaiting type selection
  const [classifyPending, setClassifyPending] = useState(null); // { jobId } when accepting a new request
  const [filters, setFilters] = useState({
    jobType: "",
    email: "",
    status: "",
    fromDate: "",
    toDate: "",
    search: "",
    assignedTo: "",
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    applyFilters();
    userService
      .getUsers()
      .then(setUsers)
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await jobService.getJobs();
      setJobs(data);
    } catch (err) {
      setError("Failed to load jobs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // jobType / email / date range / search / assignedTo are filtered server-side;
  // status is filtered client-side (the backend filter endpoint does not accept status).
  const applyFilters = async () => {
    try {
      setLoading(true);
      setError("");
      const { jobType, email, fromDate, toDate, status, search, assignedTo } =
        filters;
      const hasServerFilter =
        jobType || email || fromDate || toDate || search || assignedTo;
      const serverFilters = { jobType, email, fromDate, toDate, search };
      if (assignedTo === "unassigned") serverFilters.assignedTo = null;
      else if (assignedTo) serverFilters.assignedTo = assignedTo;
      const data = hasServerFilter
        ? await jobService.filterJobs(serverFilters)
        : await jobService.getJobs();
      setJobs(status ? data.filter((j) => j.status === status) : data);
    } catch (err) {
      setError("Failed to filter jobs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      jobType: "",
      email: "",
      status: "",
      fromDate: "",
      toDate: "",
      search: "",
      assignedTo: "",
    });
    loadJobs();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateStatus = async (jobId, newStatus, jobType = null) => {
    try {
      const updateData = { status: newStatus };
      if (jobType) {
        updateData.jobType = jobType;
      }
      await jobService.updateJob(jobId, updateData);
      await loadJobs();
    } catch (err) {
      setError("Failed to update job status");
      console.error(err);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (
      !window.confirm(
        `Delete job #${jobId}? This removes the request and its images permanently.`,
      )
    ) {
      return;
    }
    try {
      await jobService.deleteJob(jobId);
      await loadJobs();
    } catch (err) {
      setError("Failed to delete job");
      console.error(err);
    }
  };

  const handleAssignJob = async (jobId, userId) => {
    try {
      const updatedJob = await jobService.updateJob(jobId, {
        assignedTo: userId === "" ? null : Number(userId),
      });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? updatedJob : j)));
      setSelectedJob((prev) => (prev && prev.id === jobId ? updatedJob : prev));
    } catch (err) {
      setError("Failed to assign job");
      console.error(err);
    }
  };

  const handleClassifyJob = async (jobId, jobType) => {
    try {
      const updatedJob = await jobService.updateJob(jobId, {
        jobType,
        status: "in_progress",
      });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? updatedJob : j)));
      setSelectedJob((prev) => (prev && prev.id === jobId ? updatedJob : prev));
      setClassifyPending(null);
    } catch (err) {
      setError("Failed to classify job");
      console.error(err);
    }
  };

  // Per-job notes drafts, keyed by job id. Initialised lazily from job.notes.
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaved, setNoteSaved] = useState(null); // jobId that just saved
  const noteTimers = useRef({}); // debounce timers per job id

  const noteValue = (job) =>
    noteDrafts[job.id] !== undefined ? noteDrafts[job.id] : job.notes || "";

  const handleNoteChange = (jobId, value) => {
    setNoteDrafts((prev) => ({ ...prev, [jobId]: value }));
    setNoteSaved(null);
    // Debounce: save 2s after typing stops
    if (noteTimers.current[jobId]) clearTimeout(noteTimers.current[jobId]);
    noteTimers.current[jobId] = setTimeout(() => {
      handleSaveNote(jobId, value);
    }, 2000);
  };

  const handleSaveNote = async (jobId, valueOverride) => {
    const savedValue =
      valueOverride !== undefined ? valueOverride : (noteDrafts[jobId] ?? "");
    try {
      await jobService.updateJob(jobId, { notes: savedValue });
      setNoteSaved(jobId);
      // Update selectedJob so the modal shows the saved notes after draft is cleared
      setSelectedJob((prev) =>
        prev && prev.id === jobId ? { ...prev, notes: savedValue } : prev,
      );
      setNoteDrafts((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      setTimeout(() => setNoteSaved(null), 2500);
    } catch (err) {
      setError("Failed to save note");
      console.error(err);
    }
  };

  return (
    <AdminLayout title="Jobs Management">
      {/* Summary Card */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-ink-soft text-sm font-semibold uppercase tracking-wide">
              Total Jobs
            </h3>
            <p className="text-3xl font-bold text-terracotta-600">
              {jobs.length}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={loadJobs}
            >
              Refresh
            </Button>
            <Link to="/admin/jobs/create">
              <Button variant="primary" className="!px-3 !py-1.5 !text-sm">
                Create New Job
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 mb-6">
        {/* Mobile toggle */}
        <button
          className="flex items-center justify-between w-full md:hidden"
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
        >
          <span className="text-lg font-semibold text-ink-soft">
            Filter Jobs
            {Object.values(filters).some(Boolean) && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink-teal text-white text-xs">
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </span>
          <svg
            className={`w-5 h-5 text-ink-soft transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {/* Desktop heading */}
        <h3 className="hidden md:block text-lg font-semibold mb-4 text-ink-soft">
          Filter Jobs
        </h3>
        <div className={`${filtersOpen ? "block mt-4" : "hidden"} md:block`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Job Type
              </label>
              <select
                name="jobType"
                value={filters.jobType}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              >
                <option value="">All types</option>
                <option value="Requested">Requested</option>
                <option value="HomeRepair">Home Repair</option>
                <option value="CarRepair">Car Repair</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              >
                <option value="">All statuses</option>
                <option value="started">Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Email
              </label>
              <input
                type="text"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                placeholder="Search by email"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Assigned To
              </label>
              <select
                name="assignedTo"
                value={filters.assignedTo}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              >
                <option value="">Anyone</option>
                <option value="unassigned">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                From Date
              </label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                To Date
              </label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Search
              </label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search description or notes…"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
              />
            </div>{" "}
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              variant="primary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={applyFilters}
            >
              Apply Filters
            </Button>
            <Button
              variant="secondary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </div>
        </div>
        {/* end collapsible */}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Jobs Table */}
      {loading ? (
        <div className="bg-white p-8 rounded-lg shadow-md text-center text-ink-soft">
          Loading jobs...
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-md text-center text-ink-soft">
          No jobs found
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/70 border-b border-terracotta-100/60 text-left">
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  #
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Date
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Type
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Status
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Email
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Contact
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Assigned To
                </th>
                <th className="px-4 py-3 font-semibold text-ink-soft uppercase tracking-wide text-xs">
                  Images
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-terracotta-100/40">
              {jobs.map((job) => (
                <tr
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  className="hover:bg-terracotta-50/50 cursor-pointer transition-colors"
                  onMouseEnter={(e) =>
                    job.description &&
                    setTooltip({
                      text: job.description,
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }
                  onMouseMove={(e) =>
                    tooltip &&
                    setTooltip((t) =>
                      t ? { ...t, x: e.clientX, y: e.clientY } : t,
                    )
                  }
                  onMouseLeave={() => setTooltip(null)}
                >
                  <td className="px-4 py-3 font-semibold text-terracotta-600">
                    #{job.id}
                  </td>
                  <td className="px-4 py-3 text-ink-soft whitespace-nowrap">
                    {formatDate(job.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {job.jobType || <span className="text-ink-soft/50">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(job.status)}`}
                    >
                      {formatStatus(job.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink max-w-[180px] truncate">
                    {job.email}
                  </td>
                  <td className="px-4 py-3 text-ink whitespace-nowrap">
                    {job.contactNumber}
                  </td>
                  <td className="px-4 py-3 text-ink-soft text-sm whitespace-nowrap">
                    {job.assignedToName ? (
                      <span className="text-ink-teal font-medium">
                        {job.assignedToName}
                      </span>
                    ) : (
                      <span className="text-ink-soft/40">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-soft text-center">
                    {job.imagePaths?.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-terracotta-600">
                        {job.imagePaths.length}
                      </span>
                    ) : (
                      <span className="text-ink-soft/40">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Description tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 max-w-xs bg-ink text-cream text-sm rounded-xl px-3 py-2 shadow-lg pointer-events-none"
          style={{ left: tooltip.x + 14, top: tooltip.y + 14 }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobModal
          job={selectedJob}
          noteValue={noteValue}
          noteSaved={noteSaved}
          onClose={() => setSelectedJob(null)}
          onUpdateStatus={async (id, status, jobType = null) => {
            await handleUpdateStatus(id, status, jobType);
            // Reflect updated status inside the open modal
            setSelectedJob((prev) =>
              prev && prev.id === id
                ? { ...prev, status, ...(jobType && { jobType }) }
                : prev,
            );
          }}
          onDelete={async (id) => {
            await handleDeleteJob(id);
            setSelectedJob(null);
          }}
          onRequestCompletion={(jobId) => setCompletionPending({ jobId })}
          onNoteChange={handleNoteChange}
          onSaveNote={handleSaveNote}
          onOpenLightbox={setLightbox}
          onAssign={handleAssignJob}
          onRequestClassify={(jobId) => setClassifyPending({ jobId })}
          users={users}
          formatDate={formatDate}
          formatStatus={formatStatus}
          getStatusColor={getStatusColor}
          resolveImageUrl={resolveImageUrl}
        />
      )}

      {/* Completion Confirmation Modal */}
      {completionPending && (
        <CompletionConfirmModal
          jobId={completionPending.jobId}
          onConfirm={async (jobType) => {
            await handleUpdateStatus(
              completionPending.jobId,
              "completed",
              jobType,
            );
            setCompletionPending(null);
            setSelectedJob((prev) =>
              prev && prev.id === completionPending.jobId
                ? { ...prev, status: "completed", jobType }
                : prev,
            );
          }}
          onCancel={() => setCompletionPending(null)}
        />
      )}

      {/* Accept & Classify Modal (for Requested jobs) */}
      {classifyPending && (
        <ClassifyModal
          jobId={classifyPending.jobId}
          onConfirm={(jobType) =>
            handleClassifyJob(classifyPending.jobId, jobType)
          }
          onCancel={() => setClassifyPending(null)}
        />
      )}

      <Lightbox
        images={lightbox?.images ?? []}
        startIndex={lightbox?.index ?? 0}
        isOpen={lightbox !== null}
        onClose={() => setLightbox(null)}
        alt={(i) => `Job attachment ${i + 1}`}
      />
    </AdminLayout>
  );
};

/* ─── Job Detail Modal ─────────────────────────────────────────────────────── */

function JobModal({
  job,
  noteValue,
  noteSaved,
  onClose,
  onUpdateStatus,
  onDelete,
  onRequestCompletion,
  onNoteChange,
  onSaveNote,
  onOpenLightbox,
  onAssign,
  onRequestClassify,
  users,
  formatDate,
  formatStatus,
  getStatusColor,
  resolveImageUrl,
}) {
  const [jobData, setJobData] = React.useState(job);
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
  const [statusFeedback, setStatusFeedback] = React.useState(null);
  const statusDropdownRef = React.useRef(null);

  const handleStatusChange = async (action) => {
    setStatusDropdownOpen(false);
    if (action === "completed") {
      onRequestCompletion(jobData.id);
      return;
    }
    await onUpdateStatus(jobData.id, action);
    setJobData((prev) => ({ ...prev, status: action }));
    setStatusFeedback("Updated ✓");
    setTimeout(() => setStatusFeedback(null), 2500);
  };

  const getStatusDropdownColor = (status) => {
    switch (status) {
      case "started":
        return "bg-yellow-400 text-yellow-900 border-yellow-500 hover:bg-yellow-500";
      case "in_progress":
        return "bg-blue-600 text-white border-blue-700 hover:bg-blue-700";
      case "completed":
        return "bg-green-600 text-white border-green-700 hover:bg-green-700";
      case "cancelled":
        return "bg-red-600 text-white border-red-700 hover:bg-red-700";
      default:
        return "bg-gray-400 text-white border-gray-500 hover:bg-gray-500";
    }
  };

  // Fetch full job details when modal opens to ensure notes are populated
  React.useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const data = await jobService.getJob(job.id);
        setJobData(data);
      } catch (err) {
        console.error("Failed to fetch job details:", err);
        setJobData(job);
      }
    };
    fetchJobDetails();
  }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep notes, status and jobType in sync when parent updates selectedJob after any save/status change
  React.useEffect(() => {
    setJobData((prev) => ({
      ...prev,
      notes: job.notes,
      status: job.status,
      jobType: job.jobType,
    }));
  }, [job.notes, job.status, job.jobType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close status dropdown on outside click
  React.useEffect(() => {
    if (!statusDropdownOpen) return;
    const handler = (e) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(e.target)
      )
        setStatusDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [statusDropdownOpen]);

  // Close on Escape key
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-terracotta-100/60 bg-cream/60 shrink-0">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-ink leading-tight">
                Job Details
              </h2>
              <p className="text-xs text-ink-soft">
                {formatDate(jobData.createdAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Assigned To (inline in header) */}
            <select
              value={jobData.assignedTo ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                onAssign(jobData.id, val);
                setJobData((prev) => ({
                  ...prev,
                  assignedTo: val === "" ? null : Number(val),
                  assignedToName:
                    users.find((u) => u.id === Number(val))?.fullName ?? null,
                }));
              }}
              className="px-3 py-1.5 text-xs border border-terracotta-200 rounded-lg bg-white focus:outline-none focus:border-terracotta-400 transition text-ink"
            >
              <option value="">👤 Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  👤 {u.fullName}
                </option>
              ))}
            </select>
            {/* Status dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => setStatusDropdownOpen((o) => !o)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap flex items-center gap-1.5 border shadow-md transition-colors ${getStatusDropdownColor(jobData.status)}`}
              >
                {statusFeedback ?? formatStatus(jobData.status)}
                <svg
                  className="w-2.5 h-2.5 opacity-70"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-terracotta-100/60 z-20 py-1 overflow-hidden">
                  <button
                    onClick={() => handleStatusChange("in_progress")}
                    disabled={jobData.status === "in_progress"}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-terracotta-50 text-ink disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span>⏳</span> Mark In Progress
                  </button>
                  <button
                    onClick={() => handleStatusChange("completed")}
                    disabled={jobData.status === "completed"}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-green-50 text-green-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span>✅</span> Mark Completed
                  </button>
                  <button
                    onClick={() => handleStatusChange("cancelled")}
                    disabled={jobData.status === "cancelled"}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 text-red-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span>✕</span> Cancel Job
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-ink-soft/60 hover:text-ink transition-colors text-xl leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal body */}
        <div className="overflow-y-auto p-6 flex-1 space-y-5">
          {/* Contact details */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-start gap-2">
              <span className="text-ink-soft/60 mt-0.5">👤</span>
              <div>
                <p className="text-xs font-medium text-ink-soft uppercase tracking-wide">
                  Name
                </p>
                <p className="text-sm text-ink">
                  {jobData.name || <span className="text-ink-soft/40">—</span>}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-ink-soft/60 mt-0.5">✉️</span>
              <div>
                <p className="text-xs font-medium text-ink-soft uppercase tracking-wide">
                  Email
                </p>
                <p className="text-sm text-ink break-all">{jobData.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-ink-soft/60 mt-0.5">📞</span>
              <div>
                <p className="text-xs font-medium text-ink-soft uppercase tracking-wide">
                  Contact
                </p>
                <p className="text-sm text-ink">{jobData.contactNumber}</p>
              </div>
            </div>
            {jobData.jobType && (
              <div className="flex items-start gap-2">
                <span className="text-ink-soft/60 mt-0.5">🏷️</span>
                <div>
                  <p className="text-xs font-medium text-ink-soft uppercase tracking-wide">
                    Type
                  </p>
                  <p className="text-sm text-ink">{jobData.jobType}</p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-red-50  px-4 py-3 rounded-xl">
            <p className="text-xs font-medium uppercase tracking-wide mb-1">
              Description
            </p>
            <p className="text-sm text-ink whitespace-pre-wrap">
              {jobData.description}
            </p>
          </div>

          {/* Admin notes */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-ink-soft uppercase tracking-wide">
                {" "}
                Notes
              </p>
              {noteSaved === jobData.id && (
                <span className="text-xs text-green-600 font-medium">
                  Saved ✓
                </span>
              )}
            </div>
            <textarea
              value={noteValue(jobData)}
              onChange={(e) => onNoteChange(jobData.id, e.target.value)}
              placeholder="Add private notes about this job…"
              rows={3}
              aria-label={`Notes for job ${jobData.id}`}
              className="w-full px-4 py-2 text-sm rounded-xl border border-terracotta-100 bg-cream/40 focus:outline-none focus:border-terracotta-400 focus:ring-2 focus:ring-terracotta-100 transition"
            />
            {/* Images + Map side by side */}
            {(() => {
              const hasImages = jobData.imagePaths && jobData.imagePaths.length > 0;
              const mapMatch = jobData.description?.match(/^\[([^\]]+)\]/);
              const location = mapMatch?.[1]?.trim();
              if (!hasImages && !location) return null;
              return (
                <div className="flex gap-3 mt-3 items-start">
                  {hasImages && (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-2">
                        Attached Images ({jobData.imagePaths.length})
                      </p>
                      <div className="grid grid-cols-10 gap-2">
                        {jobData.imagePaths.map((imagePath, index) => (
                          <div
                            key={index}
                            className="relative aspect-square overflow-hidden rounded-lg border border-terracotta-100/60 hover:shadow-lg transition-shadow cursor-pointer group"
                            onClick={() =>
                              onOpenLightbox({ images: jobData.imagePaths, index })
                            }
                          >
                            <img
                              src={resolveImageUrl(imagePath)}
                              alt={`Job ${jobData.id} attachment ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              onError={(e) => {
                                e.target.src =
                                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23e5e7eb' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='12' fill='%23999'%3EImage Error%3C/text%3E%3C/svg%3E";
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {location && (
                    <div className="w-52 shrink-0 rounded-xl overflow-hidden border border-terracotta-100/60">
                      <div className="flex items-center justify-between px-2 py-1.5 bg-cream/60 border-b border-terracotta-100/60">
                        <p className="text-[10px] font-medium text-ink-soft uppercase tracking-wide truncate">
                          📍 {location}
                        </p>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-terracotta-600 hover:underline shrink-0 ml-1"
                        >
                          ↗
                        </a>
                      </div>
                      <iframe
                        title="Job location map"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(location)}&output=embed&z=15`}
                        width="100%"
                        height="160"
                        className="block border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Modal footer — actions */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-4 border-t border-terracotta-100/60 bg-cream/40 shrink-0">
          {jobData.jobType === "Requested" && jobData.status === "started" && (
            <button
              type="button"
              onClick={() => onRequestClassify(jobData.id)}
              className="px-5 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-700 shadow hover:bg-green-700 transition-colors"
            >
              ✅ Accept Job
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => onDelete(jobData.id)}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Completion Confirmation Modal ────────────────────────────────────────── */

function CompletionConfirmModal({ jobId, onConfirm, onCancel }) {
  const jobTypes = [
    { label: "Home Repair", value: "HomeRepair" },
    { label: "Car Repair", value: "CarRepair" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-ink">Mark as Completed</h3>
          <p className="text-sm text-ink-soft mt-1">
            Please select the job type to classify this request:
          </p>
        </div>

        <div className="space-y-2">
          {jobTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => onConfirm(type.value)}
              className="w-full px-4 py-3 text-left rounded-lg border-2 border-terracotta-100 bg-white hover:bg-terracotta-50 hover:border-terracotta-300 transition-colors font-medium text-ink"
            >
              {type.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 rounded-lg border border-terracotta-100 bg-cream/40 hover:bg-cream text-ink font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Classify Modal (Accept & Classify incoming requests) ─────────────────── */

function ClassifyModal({ jobId, onConfirm, onCancel }) {
  const jobTypes = [
    { label: "Home Repair", value: "HomeRepair" },
    { label: "Car Repair", value: "CarRepair" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-ink">Accept Job</h3>
          <p className="text-sm text-ink-soft mt-1">
            Select the job type to classify and move this request to{" "}
            <strong>In Progress</strong>:
          </p>
        </div>

        <div className="space-y-2">
          {jobTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => onConfirm(type.value)}
              className="w-full px-4 py-3 text-left rounded-lg border-2 border-terracotta-100 bg-white hover:bg-terracotta-50 hover:border-terracotta-300 transition-colors font-medium text-ink"
            >
              {type.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 rounded-lg border border-terracotta-100 bg-cream/40 hover:bg-cream text-ink font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
